import React, { useEffect, useMemo, useRef, useState } from 'react';
import { topographyApi, georefApi } from '../lib/api';
import { samplingText, smoothingText, topographyDate } from '../lib/topography-summary';
import { terrainApi } from '../lib/terrain-api';
import { THEMES, useTheme } from '../contexts/ThemeContext';
import { useOptionalToast } from './Toast';

const W = 560;
const H = 220;
const PAD = { l: 52, r: 12, t: 10, b: 28 };

const f1 = (v) => (typeof v === 'number' && Number.isFinite(v) ? v.toFixed(2) : '—');

/** Distance (m) vs elevation (m): the sampled profile as a line, electrodes as dots. */
function TopoChart({ profile, electrodes, C }) {
  const pts = profile.filter((p) => p.z != null);
  if (pts.length < 1) return <div style={{ fontSize: 12, color: C.text2 }}>No elevations sampled (the line is outside the DEM).</div>;
  const ds = profile.map((p) => p.dist);
  const zs = pts.map((p) => p.z);
  const d0 = Math.min(...ds);
  const d1 = Math.max(...ds, d0 + 1e-6);
  let z0 = Math.min(...zs);
  let z1 = Math.max(...zs);
  if (z1 - z0 < 1e-6) { z0 -= 0.5; z1 += 0.5; }
  const X = (d) => PAD.l + ((d - d0) / (d1 - d0)) * (W - PAD.l - PAD.r);
  const Y = (z) => H - PAD.b - ((z - z0) / (z1 - z0)) * (H - PAD.t - PAD.b);
  const line = pts.map((p, i) => `${i ? 'L' : 'M'}${X(p.dist).toFixed(1)},${Y(p.z).toFixed(1)}`).join(' ');
  // Electrodes plot at their chainage along the georeferenced line (`dist`, metres), the same axis
  // as the profile; local x is in file units and may be stretched by the georeference.
  const ex0 = electrodes.length ? Math.min(...electrodes.map((e) => e.x)) : 0;
  const eDist = (e) => (Number.isFinite(e.dist) ? e.dist : e.x - ex0);
  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" data-testid="topo-chart" role="img" aria-label="Elevation profile">
      <rect x={PAD.l} y={PAD.t} width={W - PAD.l - PAD.r} height={H - PAD.t - PAD.b} fill="none" stroke={C.border} />
      <path d={line} fill="none" stroke={C.accent} strokeWidth="1.5" />
      {electrodes.filter((e) => e.z != null).map((e) => (
        <circle key={e.id} cx={X(eDist(e))} cy={Y(e.z)} r="2.5" fill={C.warnText} />
      ))}
      <text x={PAD.l - 4} y={Y(z1) + 4} textAnchor="end" fontSize="10" fill={C.text2}>{z1.toFixed(1)}</text>
      <text x={PAD.l - 4} y={Y(z0)} textAnchor="end" fontSize="10" fill={C.text2}>{z0.toFixed(1)}</text>
      <text x={PAD.l} y={H - 10} fontSize="10" fill={C.text2}>{d0.toFixed(0)} m</text>
      <text x={W - PAD.r} y={H - 10} textAnchor="end" fontSize="10" fill={C.text2}>{d1.toFixed(0)} m</text>
      <text x={(W + PAD.l) / 2} y={H - 2} textAnchor="middle" fontSize="10" fill={C.text2}>distance along the line / elevation (m)</text>
    </svg>
  );
}

function saveBlob(blob, name) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}

/** Modal: sample the project DEM along a georeferenced survey line, apply it, export .trn. */
export default function TopographyPanel({ dataset, onClose, onApplied }) {
  const theme = useTheme();
  const C = theme?.C || THEMES.dark;
  const { showToast } = useOptionalToast();
  const [dem, setDem] = useState(undefined); // undefined = loading, null = none
  const [sampling, setSampling] = useState('electrodes');
  const [intervalM, setIntervalM] = useState('');
  const [smoothing, setSmoothing] = useState('none');
  const [windowM, setWindowM] = useState('');
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const [current, setCurrent] = useState(null); // stored topography block, if any
  useEffect(() => {
    let active = true;
    topographyApi.get(dataset.id)
      .then((res) => { if (active) setCurrent(res.data?.topography || null); })
      .catch(() => {});
    return () => { active = false; };
  }, [dataset.id]);

  useEffect(() => {
    let active = true;
    terrainApi.projectTerrain(dataset.project_id)
      .then((res) => { if (active) setDem(res.data?.dem || null); })
      .catch(() => { if (active) setDem(null); });
    return () => { active = false; };
  }, [dataset.project_id]);

  const body = useMemo(() => {
    const b = { sampling, smoothing };
    if (sampling === 'interval' && intervalM !== '') b.interval_m = Number(intervalM);
    if (smoothing === 'moving_average' && windowM !== '') b.window_m = Number(windowM);
    return b;
  }, [sampling, smoothing, intervalM, windowM]);

  const run = async (apply) => {
    setBusy(true);
    setError('');
    try {
      const res = await topographyApi.fromDem(dataset.id, { ...body, apply });
      setResult(res.data);
      if (apply) {
        showToast(`Topography saved: ${res.data.electrodes.length} electrodes`, 'success');
        topographyApi.get(dataset.id).then((r) => setCurrent(r.data?.topography || null)).catch(() => {});
        if (onApplied) onApplied();
      }
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not sample the DEM');
    } finally {
      setBusy(false);
    }
  };

  const download = async (trnFormat) => {
    try {
      const res = await georefApi.exportGeometry(dataset.id, 'trn', trnFormat);
      saveBlob(res.data, `${dataset.name || 'electrodes'}.trn`);
    } catch (e) {
      showToast('Terrain export failed', 'error');
    }
  };

  // Escape closes the dialog like the ✕ button. The dialog takes focus when it opens: the button
  // that opened it may sit in a dock panel whose own Escape handler stops the event (DockLayout).
  const dialogRef = useRef(null);
  useEffect(() => { dialogRef.current?.focus(); }, []);
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') { e.stopPropagation(); onClose?.(); } };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const inp = { background: C.bg3, color: C.text0, border: `1px solid ${C.border}`, borderRadius: 6, padding: '4px 8px', fontSize: 12, width: 90 };
  const btn = { background: C.bg3, color: C.text0, border: `1px solid ${C.border}`, borderRadius: 6, padding: '6px 12px', fontSize: 12, cursor: 'pointer' };
  const s = result?.stats;

  return (
    <div role="dialog" aria-modal="true" aria-label="Topography from DEM" ref={dialogRef} tabIndex={-1}
      onKeyDown={(e) => { if (e.key === 'Escape') { e.stopPropagation(); onClose?.(); } }}
      style={{ position: 'fixed', inset: 0, zIndex: 1000, outline: 'none', background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ width: 'min(640px, 100%)', maxHeight: '92vh', overflow: 'auto', background: C.bg2, border: `1px solid ${C.border}`, borderRadius: 12 }}>
        <div style={{ padding: '12px 16px', borderBottom: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: C.text0 }}>Topography from DEM — {dataset.name}</div>
          <button onClick={onClose} aria-label="Close" style={{ background: 'none', border: 'none', color: C.text2, cursor: 'pointer', fontSize: 16 }}>✕</button>
        </div>
        <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12, color: C.text1, fontSize: 12 }}>
          <div data-testid="topo-dem">
            {dem === undefined ? 'Loading project DEM…'
              : dem ? `Project DEM: ${dem.name || dem.dataset_id}`
                : <>No project DEM. Mark a raster as the DEM on the <a href="/datasets" style={{ color: C.accent }}>Datasets page</a> first.</>}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <input type="radio" name="topo-sampling" data-testid="sampling-electrodes" checked={sampling === 'electrodes'} onChange={() => setSampling('electrodes')} />
              At each electrode
            </label>
            <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <input type="radio" name="topo-sampling" data-testid="sampling-interval" checked={sampling === 'interval'} onChange={() => setSampling('interval')} />
              Every
              <input type="number" min="0.1" max="1000" step="any" placeholder="spacing" data-testid="interval-m" style={inp}
                disabled={sampling !== 'interval'} value={intervalM} onChange={(e) => setIntervalM(e.target.value)} />
              m along the line
            </label>
          </div>

          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            Smoothing
            <select value={smoothing} onChange={(e) => setSmoothing(e.target.value)} data-testid="smoothing" style={{ ...inp, width: 150 }}>
              <option value="none">None</option>
              <option value="moving_average">Moving average</option>
            </select>
            {smoothing === 'moving_average' && (
              <>
                window
                <input type="number" min="0.1" step="any" placeholder="3 × spacing" data-testid="window-m" style={inp}
                  value={windowM} onChange={(e) => setWindowM(e.target.value)} />
                m
              </>
            )}
          </div>

          {current && (
            <div data-testid="topo-current" style={{ color: C.text2 }}>
              Current: applied {topographyDate(current)} ({samplingText(current)}{smoothingText(current)}). Applying again replaces it.
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button style={btn} onClick={() => run(false)} disabled={busy || dem === null} data-testid="topo-preview">Preview</button>
            <button style={btn} onClick={() => run(true)} disabled={busy || dem === null || !result} data-testid="topo-apply">Apply to survey</button>
            <button style={btn} onClick={() => download(2)} data-testid="topo-download-trn">Download .trn (EarthImager 2D)</button>
          </div>
          {error && <div style={{ color: C.red }} data-testid="topo-error">{error}</div>}

          {result && (
            <div>
              <TopoChart profile={result.profile} electrodes={result.electrodes} C={C} />
              <div data-testid="topo-stats" style={{ marginTop: 4 }}>
                min {f1(s.min)} m · max {f1(s.max)} m · relief {f1(s.relief)} m · missing {s.missing}
              </div>
            </div>
          )}

          <div style={{ color: C.text2 }}>
            <button onClick={() => download(1)} style={{ background: 'none', border: 'none', color: C.accent, cursor: 'pointer', padding: 0, fontSize: 11, textDecoration: 'underline' }}>
              map-coordinate .trn (format 1)
            </button>
          </div>
          <div style={{ color: C.text2, fontSize: 11 }}>
            Next: run the inversion in EarthImager 2D with the .stg and this .trn, then Insert › Inversion results to view the .out here.
          </div>
        </div>
      </div>
    </div>
  );
}
